import React, { useState, useEffect } from 'react';
import {
  Dialog,
  Box,
  Typography,
  alpha,
  IconButton,
  Button,
  Chip,
  Divider,
  useMediaQuery,
  Avatar,
  CircularProgress,
} from '@mui/material';
import { 
  Close,
  CheckCircle,
  FilterList,
  Delete,
  Email,
  Person,
  CalendarToday,
  Star,
  Refresh,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTheme as useMuiTheme } from '@mui/material/styles';

// Import your logo
import logo from '../../assets/Text-WFHCafe_WObg.png';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface Feedback {
  _id: string;
  userId?: string;
  username: string;
  email: string;
  feedback: string;
  timestamp: string;
  status: 'new' | 'reviewed' | 'resolved';
  createdAt: string;
  updatedAt: string;
}

interface FeedbackActionsModalProps {
  open: boolean;
  onClose: () => void;
  feedback: Feedback | null;
  onStatusUpdate: (id: string, status: 'new' | 'reviewed' | 'resolved') => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  isDark: boolean;
}

interface UserProfile {
  id: string;
  username: string;
  profilePicture?: string;
}

const FeedbackActionsModal: React.FC<FeedbackActionsModalProps> = ({
  open,
  onClose,
  feedback,
  onStatusUpdate,
  onDelete,
  isDark,
}) => {
  // All hooks must be called unconditionally
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('md'));
  
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  const goldGradient = `linear-gradient(135deg, ${goldColor} 0%, #D4C690 100%)`;
  
  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: 'auto',
    maxWidth: 'calc(100vw - 32px)',
    maxHeight: isMobile ? '90vh' : '80vh',
    margin: 'auto',
    mx: isMobile ? 2 : 'auto',
    overflow: 'hidden',
    padding: 0,
    position: 'relative',
  };

  // Fetch user profile when feedback changes
  useEffect(() => {
    if (feedback?.userId) {
      fetchUserProfile(feedback.userId);
    } else {
      setUserProfile(null);
    }
  }, [feedback]);

  const fetchUserProfile = async (userId: string) => {
    setLoadingProfile(true);
    try {
      const token = localStorage.getItem('token');
      // Try to get user profile from all users list
      const response = await fetch(`${API_URL}/api/user/all?limit=1000`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.users) {
          const foundUser = data.users.find((u: any) => u._id === userId);
          if (foundUser) {
            setUserProfile({
              id: foundUser._id,
              username: foundUser.username,
              profilePicture: foundUser.profilePicture
            });
          }
        }
      }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    } finally {
      setLoadingProfile(false);
    }
  };

  // Now check if feedback is null - return early but AFTER all hooks
  if (!feedback) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: modalStyle,
        }}
        maxWidth={false}
      >
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ color: isDark ? '#fff' : deepGreen }}>
            No feedback selected
          </Typography>
          <Button 
            onClick={onClose} 
            sx={{ 
              mt: 2,
              color: goldColor,
              '&:hover': { bgcolor: alpha(goldColor, 0.1) }
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>
    );
  }

  const getStatusColor = () => {
    // Use theme colors instead of different colors
    return isDark ? goldColor : deepGreen;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'reviewed': return <FilterList fontSize={isMobile ? "small" : "medium"} />;
      case 'resolved': return <CheckCircle fontSize={isMobile ? "small" : "medium"} />;
      default: return <Star fontSize={isMobile ? "small" : "medium"} />;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isMobile) {
        // Short format for mobile
        return format(date, 'MMM d, yyyy');
      }
      return format(date, 'PPP p');
    } catch {
      return dateString;
    }
  };

  const getInitials = (username: string) => {
    return username
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleStatusUpdate = async (status: 'new' | 'reviewed' | 'resolved') => {
    setActionLoading(`status-${status}`);
    try {
      await onStatusUpdate(feedback._id, status);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    setActionLoading('delete');
    try {
      await onDelete(feedback._id);
      onClose();
    } finally {
      setActionLoading(null);
    }
  };

  const gradientButtonStyle = {
    background: isDark ? goldGradient : lokiGradient,
    color: isDark ? deepGreen : goldColor,
    borderRadius: '50px',
    fontWeight: 800,
    textTransform: 'none',
    fontSize: isMobile ? '0.85rem' : '1rem',
    padding: isMobile ? '8px 16px' : '12px 32px',
    letterSpacing: '-0.02em',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 8px 32px ${alpha(goldColor, 0.25)}`
      : `0 8px 32px ${alpha(deepGreen, 0.2)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: isDark
        ? `0 12px 40px ${alpha(goldColor, 0.35)}`
        : `0 12px 40px ${alpha(deepGreen, 0.3)}`,
      background: isDark
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.9)} 0%, #E5D9A8 100%)`
        : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
    },
    '&:active': {
      transform: 'translateY(0)',
    }
  };

  const statusButtonStyle = {
    color: getStatusColor(),
    borderColor: alpha(getStatusColor(), 0.3),
    borderRadius: '50px',
    fontWeight: 700,
    px: isMobile ? 2 : 3,
    py: isMobile ? 0.75 : 1,
    fontSize: isMobile ? '0.8rem' : '0.95rem',
    minWidth: isMobile ? 'auto' : '140px',
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: getStatusColor(),
      bgcolor: alpha(getStatusColor(), isDark ? 0.1 : 0.05),
    },
    '&:disabled': {
      color: alpha(getStatusColor(), 0.5),
      borderColor: alpha(getStatusColor(), 0.2),
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
      maxWidth={false}
      sx={{
        '& .MuiDialog-container': {
          alignItems: isMobile ? 'flex-end' : 'center',
        }
      }}
    >
      {/* Single Content Container with Logo */}
      <Box sx={{
        p: isMobile ? 2 : 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '600px',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
        maxWidth: isMobile ? '350px' : '700px',
        minWidth: isMobile ? '350px' : '600px',
      }}>
        {/* Logo and Close Button - Mobile optimized */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: isMobile ? 2 : 4,
          position: 'sticky',
          top: 0,
          
          zIndex: 10,
          py: isMobile ? 1 : 0,
        }}>
          <Box
            component="img"
            src={logo}
            alt="WFHCafe Logo"
            sx={{
              height: isMobile ? 30 : 45,
              width: 'auto',
              filter: isDark ? 'brightness(1.1)' : 'none',
              maxWidth: isMobile ? '60%' : 'auto',
            }}
          />
          
          <IconButton 
            onClick={onClose} 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              padding: isMobile ? '4px' : '8px',
              '&:hover': { 
                color: goldColor,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
              }
            }}
          >
            <Close fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
        </Box>

        {/* Title - Mobile optimized */}
        <Box sx={{ mb: isMobile ? 2 : 4 }}>
          <Typography 
            variant={isMobile ? "h6" : "h4"} 
            sx={{ 
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: isDark ? '#fff' : deepGreen,
              fontSize: isMobile ? '1.25rem' : '2rem',
              lineHeight: 1.2,
            }}
          >
            Feedback Details
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              fontSize: isMobile ? '0.8rem' : '1rem',
              mt: 0.5,
            }}
          >
            Review and manage user feedback
          </Typography>
        </Box>

        {/* User Info Section - Mobile optimized */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? 1.5 : 2, 
          mb: 3,
          p: isMobile ? 2 : 3,
          borderRadius: '16px',
          backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.02)',
          border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
          flexDirection: isMobile ? 'column' : 'row',
          textAlign: isMobile ? 'center' : 'left',
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? 1.5 : 2,
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : 'flex-start',
          }}>
            {loadingProfile ? (
              <CircularProgress size={isMobile ? 40 : 48} sx={{ color: goldColor }} />
            ) : (
              <Avatar
                src={userProfile?.profilePicture}
                sx={{
                  bgcolor: alpha(goldColor, 0.1),
                  color: goldColor,
                  fontWeight: 700,
                  width: isMobile ? 50 : 60,
                  height: isMobile ? 50 : 60,
                  fontSize: isMobile ? '1rem' : '1.2rem',
                  border: `2px solid ${alpha(goldColor, 0.3)}`,
                }}
              >
                {getInitials(feedback.username)}
              </Avatar>
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 700,
                color: isDark ? '#fff' : deepGreen,
                mb: 0.5,
                fontSize: isMobile ? '1rem' : '1.25rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {feedback.username}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: isMobile ? '0.8rem' : '0.95rem',
                justifyContent: isMobile ? 'center' : 'flex-start',
                flexWrap: 'wrap',
              }}>
                <Email sx={{ fontSize: isMobile ? 14 : 16 }} />
                <Box component="span" sx={{ 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: isMobile ? '200px' : 'none',
                }}>
                  {feedback.email}
                </Box>
              </Typography>
            </Box>
          </Box>
          <Chip
            icon={getStatusIcon(feedback.status)}
            label={feedback.status}
            sx={{
              bgcolor: alpha(getStatusColor(), isDark ? 0.15 : 0.1),
              color: getStatusColor(),
              fontWeight: 700,
              fontSize: isMobile ? '0.8rem' : '0.95rem',
              textTransform: 'capitalize',
              border: `2px solid ${alpha(getStatusColor(), 0.3)}`,
              height: isMobile ? 32 : 36,
              '& .MuiChip-icon': {
                color: getStatusColor(),
              },
              mt: isMobile ? 1 : 0,
            }}
          />
        </Box>

        {/* Metadata - Mobile optimized */}
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: 1,
          mb: 3,
          justifyContent: isMobile ? 'center' : 'flex-start',
        }}>
          <Chip
            icon={<CalendarToday sx={{ fontSize: isMobile ? 14 : 16 }} />}
            label={`Submitted: ${formatDate(feedback.timestamp)}`}
            variant="outlined"
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              borderColor: alpha(isDark ? '#fff' : deepGreen, 0.2),
              fontSize: isMobile ? '0.75rem' : '0.9rem',
              maxWidth: '100%',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                px: 1,
              }
            }}
          />
          {feedback.userId && (
            <Chip
              icon={<Person sx={{ fontSize: isMobile ? 14 : 16 }} />}
              label={`ID: ${feedback.userId.substring(0, 6)}...`}
              variant="outlined"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                borderColor: alpha(isDark ? '#fff' : deepGreen, 0.2),
                fontSize: isMobile ? '0.75rem' : '0.9rem',
                maxWidth: '100%',
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  px: 1,
                }
              }}
            />
          )}
        </Box>

        <Divider sx={{ 
          mb: 3, 
          borderColor: alpha(isDark ? '#fff' : deepGreen, 0.1) 
        }} />

        {/* Feedback Content - Mobile optimized */}
        <Box sx={{ mb: 4 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 700,
              color: isDark ? '#fff' : deepGreen,
              mb: 2,
              fontSize: isMobile ? '1rem' : '1.1rem',
            }}
          >
            Feedback Message
          </Typography>
          <Box sx={{
            p: isMobile ? 2 : 3,
            borderRadius: '16px',
            bgcolor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(5,24,20,0.02)',
            border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
            minHeight: isMobile ? 100 : 120,
          }}>
            <Typography sx={{ 
              color: isDark ? 'rgba(255,255,255,0.9)' : deepGreen,
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              fontSize: isMobile ? '0.9rem' : '1rem',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
            }}>
              {feedback.feedback}
            </Typography>
          </Box>
        </Box>

        {/* Status Actions - Mobile optimized */}
        <Box sx={{mb: 2}}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 700,
              color: isDark ? '#fff' : deepGreen,
              mb: 2,
              fontSize: isMobile ? '1rem' : '1.1rem',
            }}
          >
            Update Status
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            gap: isMobile ? 1 : 2, 
            flexWrap: 'wrap',
            justifyContent: isMobile ? 'center' : 'flex-start',
          }}>
            {feedback.status !== 'new' && (
              <Button
                variant="outlined"
                onClick={() => handleStatusUpdate('new')}
                disabled={actionLoading !== null}
                startIcon={actionLoading === 'status-new' ? 
                  <CircularProgress size={isMobile ? 16 : 20} /> : 
                  <Refresh fontSize={isMobile ? "small" : "medium"} />
                }
                sx={statusButtonStyle}
              >
                {isMobile ? 'New' : 'Mark as New'}
              </Button>
            )}
            {feedback.status !== 'reviewed' && (
              <Button
                variant="outlined"
                onClick={() => handleStatusUpdate('reviewed')}
                disabled={actionLoading !== null}
                startIcon={actionLoading === 'status-reviewed' ? 
                  <CircularProgress size={isMobile ? 16 : 20} /> : 
                  <FilterList fontSize={isMobile ? "small" : "medium"} />
                }
                sx={statusButtonStyle}
              >
                {isMobile ? 'Reviewed' : 'Mark as Reviewed'}
              </Button>
            )}
            {feedback.status !== 'resolved' && (
              <Button
                variant="outlined"
                onClick={() => handleStatusUpdate('resolved')}
                disabled={actionLoading !== null}
                startIcon={actionLoading === 'status-resolved' ? 
                  <CircularProgress size={isMobile ? 16 : 20} /> : 
                  <CheckCircle fontSize={isMobile ? "small" : "medium"} />
                }
                sx={statusButtonStyle}
              >
                {isMobile ? 'Resolved' : 'Mark as Resolved'}
              </Button>
            )}
          </Box>
        </Box>

        {/* Buttons at Bottom - Mobile optimized */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 'auto',
          pt: 1,
          flexWrap: isMobile ? 'wrap-reverse' : 'nowrap',
          gap: isMobile ? 2 : 0,
          flexDirection: isMobile ? 'column-reverse' : 'row',
        }}>
          <Button
            onClick={onClose}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontWeight: 700,
              borderRadius: '50px',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0.75 : 1,
              fontSize: isMobile ? '0.85rem' : '1rem',
              textTransform: 'none',
              width: isMobile ? '100%' : 'auto',
              order: isMobile ? 2 : 1,
              '&:hover': {
                bgcolor: alpha(goldColor, 0.05)
              }
            }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={actionLoading !== null}
            startIcon={actionLoading === 'delete' ? 
              <CircularProgress size={isMobile ? 16 : 20} color="inherit" /> : 
              <Delete fontSize={isMobile ? "small" : "medium"} />
            }
            sx={{
              ...gradientButtonStyle,
              width: isMobile ? '100%' : 'auto',
              order: isMobile ? 1 : 2,
              '&:disabled': {
                background: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.3)',
                color: isDark ? 'rgba(5, 24, 20, 0.5)' : 'rgba(196, 182, 111, 0.5)',
                transform: 'none',
                boxShadow: 'none',
              }
            }}
          >
            Delete Feedback
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default FeedbackActionsModal;